(function () {
  'use strict';

  var MOBILE_MAX_WIDTH = 1079;
  var DRAG_INTENT_THRESHOLD = 6; // px of movement before we decide horizontal vs vertical intent
  var SLIDE_DISTANCE_THRESHOLD = 0.2; // fraction of a slide width needed to commit to a slide change
  var FLICK_VELOCITY_THRESHOLD = 0.35; // px/ms — a fast flick changes slide regardless of distance dragged
  var ANIMATION_DURATION = 500; // ms

  function easeOutSine(t) {
    return Math.sin((t * Math.PI) / 2);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function GallerySwipe(list) {
    this.list = list;
    this.rafId = null;
    this.directionLocked = null;
    this.velocity = 0;
    this.gestureItems = null; // slide elements, snapshotted fresh per-gesture (variant changes reorder them)
    this.mobileQuery = window.matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)');

    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.updateDraggable = this.updateDraggable.bind(this);

    list.addEventListener('touchstart', this.onTouchStart, { passive: true });

    // Native image dragging fights this custom swipe gesture, so disable it only
    // where the gesture is actually active (mobile/tablet). Desktop has no touch-drag
    // here, so leave native image dragging alone there.
    this.mobileQuery.addEventListener('change', this.updateDraggable);
    this.updateDraggable();
  }

  GallerySwipe.prototype.isActive = function () {
    return (
      this.list.children.length > 1 &&
      this.mobileQuery.matches &&
      this.list.scrollWidth > this.list.clientWidth + 1
    );
  };

  GallerySwipe.prototype.updateDraggable = function () {
    var isMobile = this.mobileQuery.matches;
    Array.prototype.forEach.call(this.list.querySelectorAll('img'), function (img) {
      img.draggable = !isMobile;
    });
  };

  GallerySwipe.prototype.onTouchStart = function (event) {
    if (!this.isActive()) return;
    this.cancelAnimation();

    this.gestureItems = Array.prototype.slice.call(this.list.children);

    var touch = event.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.startScrollLeft = this.list.scrollLeft;
    this.lastX = touch.clientX;
    this.lastT = event.timeStamp;
    this.lastDx = 0;
    this.velocity = 0;
    this.directionLocked = null;

    this.list.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.list.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.list.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
  };

  GallerySwipe.prototype.onTouchMove = function (event) {
    var touch = event.touches[0];
    var dx = touch.clientX - this.startX;
    var dy = touch.clientY - this.startY;

    if (!this.directionLocked) {
      if (Math.abs(dx) < DRAG_INTENT_THRESHOLD && Math.abs(dy) < DRAG_INTENT_THRESHOLD) return;
      this.directionLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    if (this.directionLocked !== 'x') return;

    // Block the browser's native touch-scroll so the slide can't be dragged
    // or follow the finger at all — only the direction/velocity is tracked
    // here; the actual transition to the next/previous slide happens as a
    // single animated jump once the gesture ends (see onTouchEnd).
    event.preventDefault();

    var dt = event.timeStamp - this.lastT;
    if (dt > 0) {
      var instantVelocity = (touch.clientX - this.lastX) / dt;
      this.velocity = this.velocity * 0.7 + instantVelocity * 0.3;
    }
    this.lastX = touch.clientX;
    this.lastT = event.timeStamp;
    this.lastDx = dx;
  };

  GallerySwipe.prototype.onTouchEnd = function () {
    this.list.removeEventListener('touchmove', this.onTouchMove);
    this.list.removeEventListener('touchend', this.onTouchEnd);
    this.list.removeEventListener('touchcancel', this.onTouchEnd);

    if (this.directionLocked !== 'x') {
      // Release any override left engaged by an animation this gesture interrupted —
      // no animateTo call is coming to do it for us.
      this.endOverride();
      return;
    }

    var items = this.gestureItems;
    var startIndex = this.nearestIndexTo(items, this.startScrollLeft);
    var direction = 0;

    if (Math.abs(this.velocity) > FLICK_VELOCITY_THRESHOLD) {
      // A fast flick commits to one slide in the flick direction regardless of distance dragged.
      direction = this.velocity < 0 ? 1 : -1;
    } else {
      var slideWidth = this.slideWidthFrom(items, startIndex);
      if (slideWidth && Math.abs(this.lastDx) >= slideWidth * SLIDE_DISTANCE_THRESHOLD) {
        direction = this.lastDx < 0 ? 1 : -1;
      }
    }

    this.animateTo(items, startIndex + direction);
  };

  GallerySwipe.prototype.nearestIndexTo = function (items, scrollLeft) {
    var nearestIndex = 0;
    var nearestDistance = Infinity;
    items.forEach(function (item, index) {
      var distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  };

  GallerySwipe.prototype.slideWidthFrom = function (items, index) {
    var current = items[index];
    var neighbor = items[index + 1] || items[index - 1];
    if (!current || !neighbor) return this.list.clientWidth;
    return Math.abs(neighbor.offsetLeft - current.offsetLeft);
  };

  GallerySwipe.prototype.animateTo = function (items, index) {
    var clamped = Math.max(0, Math.min(index, items.length - 1));
    var target = items[clamped].offsetLeft;
    var from = this.list.scrollLeft;
    var distance = target - from;
    var list = this.list;
    var self = this;

    // Disable native scroll-snap/smooth-scroll only for the duration of this
    // JS-driven transition, so they don't fight our per-frame scrollLeft updates.
    this.beginOverride();

    if (Math.abs(distance) < 1 || prefersReducedMotion()) {
      list.scrollLeft = target;
      this.endOverride();
      return;
    }

    var start = null;

    var step = function (timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / ANIMATION_DURATION, 1);
      list.scrollLeft = from + distance * easeOutSine(progress);
      if (progress < 1) {
        self.rafId = requestAnimationFrame(step);
      } else {
        self.rafId = null;
        self.endOverride();
      }
    };

    this.rafId = requestAnimationFrame(step);
  };

  // While we're driving scrollLeft ourselves via animateTo's rAF loop, disable native
  // scroll-snap and scroll-behavior:smooth — both apply to direct scrollLeft assignment too,
  // and would otherwise fight our per-frame updates with their own smoothing/snapping.
  GallerySwipe.prototype.beginOverride = function () {
    this.list.style.scrollSnapType = 'none';
    this.list.style.scrollBehavior = 'auto';
  };

  GallerySwipe.prototype.endOverride = function () {
    this.list.style.scrollSnapType = '';
    this.list.style.scrollBehavior = '';
  };

  // Deliberately leaves scroll-snap/scroll-behavior overridden when interrupting an
  // in-flight animation — the list is sitting at a non-snap-point scrollLeft at that
  // moment, and restoring native snap immediately would let the browser correct toward
  // the nearest slide right as the new gesture starts, producing a visible twitch.
  // The override is released once the gesture actually settles (see onTouchEnd/animateTo).
  GallerySwipe.prototype.cancelAnimation = function () {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  function initGallerySwipe() {
    document
      .querySelectorAll('slider-component[id^="GalleryViewer-"] ul.slider--mobile')
      .forEach(function (list) {
        if (list.dataset.swipeEnhanced) return;
        list.dataset.swipeEnhanced = 'true';
        new GallerySwipe(list);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallerySwipe);
  } else {
    initGallerySwipe();
  }

  document.addEventListener('shopify:section:load', initGallerySwipe);
})();
